import type { TenantStateSet } from '@chatbot/shared-types';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { toast } from '../components/Toast.tsx';
import { TicketDetailPanel } from '../components/tickets/TicketDetailPanel.tsx';
import { TicketRowItem } from '../components/tickets/TicketRow.tsx';
import { useGlobalWebSocket } from '../hooks/WebSocketProvider.tsx';
import { useTenantContext } from '../hooks/useTenantContext.ts';
import { api } from '../lib/api.ts';
import { sonarConversacionNueva } from '../lib/inbox-sound.ts';
import type { TicketRow } from '../lib/types.ts';

/**
 * La cola de tickets del tenant.
 *
 * El filtro manda `abiertos=true|false`, que en el backend es el prefijo del
 * sort key del índice —`terminal` congelado al escribir—. **Nunca un nombre de
 * estado**: un tenant que llama "En taller" al suyo entra por acá sin que este
 * archivo sepa que ese estado existe.
 */
export function TicketsPage() {
  const { activeTenantId, tenantIds } = useTenantContext();
  // El tenant vive en la URL, igual que en la bandeja. Además de resolver el
  // cambio, hace la vista compartible: un enlace a los tickets de un tenant
  // abre ese tenant y no el que tenga activo quien lo reciba.
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? activeTenantId ?? tenantIds[0] ?? '';

  const [soloAbiertos, setSoloAbiertos] = useState(true);
  const [items, setItems] = useState<TicketRow[]>([]);
  const [states, setStates] = useState<TenantStateSet>([]);
  const [seleccionado, setSeleccionado] = useState<TicketRow | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    if (!tenantId) {
      setCargando(false);
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const res = await api.tickets.list(tenantId, { abiertos: soloAbiertos });
      // Sin reordenar: el backend ya los trae por antigüedad y la paginación
      // avanza en ESE orden. Ordenar acá rompería la página siguiente.
      setItems(res.items);
      setStates(res.states);
      setSeleccionado((actual) =>
        actual ? (res.items.find((t) => t.caseId === actual.caseId) ?? null) : null,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCargando(false);
    }
  }, [tenantId, soloAbiertos]);

  const { subscribe } = useGlobalWebSocket();

  useEffect(() => {
    if (!tenantId) return;
    return subscribe((event) => {
      // El tenantId se compara SIEMPRE: sin esto sonaría por tickets de otro
      // negocio, que además es una fuga de que ese negocio existe (regla A1).
      if (event.type !== 'ticket.opened' || event.tenantId !== tenantId) return;

      // El mismo tono que la bandeja, y respeta el mismo silencio: un operador
      // que silenció el panel no quiere que los tickets lo salteen.
      sonarConversacionNueva();
      toast.success(`Ticket nuevo: ${event.payload.summary}`);
      recargar();
    });
  }, [subscribe, tenantId, recargar]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  async function asignar(ticket: TicketRow, assignedTo: string) {
    try {
      await api.tickets.assign(tenantId, ticket, assignedTo);
      toast.success('Ticket asignado');
      await recargar();
    } catch (err) {
      toast.error(`No se pudo asignar: ${(err as Error).message}`);
    }
  }

  async function reabrir(ticket: TicketRow, status: string) {
    try {
      await api.tickets.reopen(tenantId, ticket, status);
      toast.success('Ticket reabierto');
      await recargar();
    } catch (err) {
      toast.error(`No se pudo reabrir: ${(err as Error).message}`);
    }
  }

  return (
    <AppShell>
      <PageHeader
        title="Tickets"
        subtitle="Lo que el bot no pudo resolver, con la conversación que lo originó al lado."
      />

      {/* La página no tenía selector de tenant, así que mostraba los tickets del
          que estuviera activo sin forma de cambiarlo desde acá — y un ticket de
          otro tenant se leía como "no hay tickets". La bandeja sí lo tiene; esto
          copia ese patrón. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {tenantIds.length > 1 ? (
          <select
            value={tenantId}
            onChange={(e) => setSearchParams({ tenantId: e.target.value })}
            aria-label="Tenant"
            className="select-native rounded border border-edge-subtle bg-surface-base px-2 py-1 text-caption text-content-primary"
          >
            {tenantIds.map((tid) => (
              <option key={tid} value={tid}>
                {tid}
              </option>
            ))}
          </select>
        ) : null}

        <div className="flex gap-2">
          {(
            [
              { label: 'Abiertos', abiertos: true },
              { label: 'Todos', abiertos: false },
            ] as const
          ).map((f) => (
            <button
              key={f.label}
              type="button"
              onClick={() => setSoloAbiertos(f.abiertos)}
              aria-pressed={soloAbiertos === f.abiertos}
              className={`rounded-full px-3 py-1 text-caption ${
                soloAbiertos === f.abiertos
                  ? // `bg-surface-brand` NO existe: el design system define
                    // `surface.brand-soft` y nada más, así que Tailwind nunca
                    // generó esa clase. El chip activo quedaba sin fondo, con
                    // `text-content-inverse` (#f8fafc) sobre blanco — invisible.
                    // Es el par que ya usa el chip activo de la bandeja.
                    'bg-primary-600 text-white'
                  : 'bg-surface-sunken text-content-secondary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-2">
          <ErrorAlert message={error} />
          <button
            type="button"
            onClick={recargar}
            className="rounded-md border border-edge px-3 py-1 text-caption text-content-primary"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {cargando && !error ? <Skeleton /> : null}

      {!cargando && !error && items.length === 0 ? (
        <EmptyState message="Todavía no hay tickets. Aparecen acá cuando el bot registra un problema que no pudo resolver en la conversación." />
      ) : null}

      {!cargando && !error && items.length > 0 ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-h-0 overflow-auto rounded-lg border border-edge-subtle">
            {items.map((t) => (
              <TicketRowItem
                key={t.caseId}
                ticket={t}
                states={states}
                seleccionado={seleccionado?.caseId === t.caseId}
                onSelect={setSeleccionado}
              />
            ))}
          </div>
          {seleccionado ? (
            <TicketDetailPanel
              ticket={seleccionado}
              states={states}
              tenantId={tenantId}
              onAssign={asignar}
              onReopen={reabrir}
            />
          ) : null}
        </div>
      ) : null}
    </AppShell>
  );
}
